import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import {
  Configuration,
  RegistryDetails,
  Section,
  SectionTypes,
  StripeRegistryDetails,
} from "../../../resources/modelTypes";
import { useGuestStripe } from "../GuestStripeContext";
import { useWebsiteWidth } from "../WebsiteWidthContext";
import { DisplayStripeRegistrySection } from "./DisplayStripeRegistrySection";
import { RegistryCard } from "./RegistryCard";
import { css } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import { memo } from "react";
import React from "react";

const CARD_WIDTH_PX = 256;
const DESKTOP_GAP_PX = 32;

const styles = {
  wrapper: (isMobile: boolean, columns: number) => css`
    display: grid;
    gap: ${isMobile ? theme.spacing.x2 : theme.spacing.x4};
    grid-template-columns: repeat(
      ${columns},
      ${isMobile
        ? `calc((100% - ${theme.spacing.x2}) / 2)`
        : styleUtils.rem(CARD_WIDTH_PX)}
    );
    ${isMobile
      ? `
      justify-content: center;
      width: 100%;`
      : ""}
  `,
  container: css`
    align-items: center;
    display: flex;
    justify-content: center;
    width: 100%;
  `,
};

export type GroupedRegistriesSection = {
  registries: {
    data: Section;
  }[];
};

type PropTypes = {
  configuration: Configuration;
  isPreviewClickable?: boolean;
  section: GroupedRegistriesSection;
};

function RegistriesGrid(props: PropTypes) {
  const { configuration, isPreviewClickable, section } = props;
  const { isPreview } = useIsPreviewOrDemo();
  const { isStripeSetupComplete } = useGuestStripe();
  const width = useWebsiteWidth();
  const isMobile: boolean = width !== null && width < 600;

  const visibleRegistries = section.registries?.filter(({ data }) => {
    if (data.sectionType === SectionTypes.StripeRegistry) {
      // Host-toggled visibility, plus live-only hide while Stripe onboarding
      // is incomplete so column math matches rendered children.
      if ((data.sectionDetails as StripeRegistryDetails).isVisible === false) {
        return false;
      }
      return isPreview || isStripeSetupComplete;
    }
    return true;
  });

  const registryCount = visibleRegistries?.length || 0;
  let columns: number;

  if (isMobile) {
    columns = Math.min(registryCount, 2);
  } else {
    const maxFittingCols = width
      ? Math.floor((width + DESKTOP_GAP_PX) / (CARD_WIDTH_PX + DESKTOP_GAP_PX))
      : 3;
    columns = Math.min(registryCount, Math.max(maxFittingCols, 1));
  }

  return (
    <div css={styles.container}>
      <div css={styles.wrapper(isMobile, columns)}>
        {visibleRegistries?.map(({ data }) =>
          data.sectionType === SectionTypes.StripeRegistry ? (
            <DisplayStripeRegistrySection
              configuration={configuration}
              section={data as Section}
              key={`stripe_${data.id}`}
            />
          ) : (
            <RegistryCard
              configuration={configuration}
              details={data.sectionDetails as RegistryDetails}
              isPreviewClickable={isPreviewClickable}
              section={data as Section}
              key={`reg_${data.id}`}
            />
          ),
        )}
      </div>
    </div>
  );
}

const MemoizedRegistriesGrid = memo(RegistriesGrid);

export default MemoizedRegistriesGrid;
